import { bind } from 'bind-event-listener';

type LatestUserSubmission = {
	conversationId: string;
	humanMessageId: string;
};

const latestUserSubmissionByConversation = new Map<string, LatestUserSubmission>();

/**
 * Retains the latest non-UGC user submission identity for lazy Chat consumers.
 * Returns an identity-bound cleanup callback so an older stream cannot clear a newer submission.
 */
export const userSubmissionLifecycle = {
	recordLatestUserSubmission(
		submission: LatestUserSubmission,
		{ abortSignal }: { abortSignal?: AbortSignal } = {},
	): () => void {
		latestUserSubmissionByConversation.set(submission.conversationId, submission);

		const clearIfStillLatest = (): void => {
			if (latestUserSubmissionByConversation.get(submission.conversationId) === submission) {
				latestUserSubmissionByConversation.delete(submission.conversationId);
			}
		};
		if (abortSignal?.aborted) {
			clearIfStillLatest();
		} else if (abortSignal) {
			bind(abortSignal, {
				type: 'abort',
				listener: clearIfStillLatest,
				options: { once: true },
			});
		}

		return clearIfStillLatest;
	},

	getLatestUserSubmission(conversationId: string): LatestUserSubmission | undefined {
		return latestUserSubmissionByConversation.get(conversationId);
	},

	replaceHumanMessageId({
		conversationId,
		oldId,
		newId,
	}: {
		conversationId: string;
		oldId: string;
		newId: string;
	}): void {
		const submission = latestUserSubmissionByConversation.get(conversationId);
		if (submission?.humanMessageId === oldId) {
			submission.humanMessageId = newId;
		}
	},

	clearLatestUserSubmission({
		conversationId,
		humanMessageId,
	}: {
		conversationId: string;
		humanMessageId?: string;
	}): void {
		const submission = latestUserSubmissionByConversation.get(conversationId);
		if (!humanMessageId || submission?.humanMessageId === humanMessageId) {
			latestUserSubmissionByConversation.delete(conversationId);
		}
	},
};
